import { useEffect, useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Loader2, MapPinned, CheckCircle2, AlertTriangle, FileSpreadsheet } from "lucide-react";
import { saveBlob } from "@/lib/labels";

const csvCell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
const remainingCsv = (rows) => new Blob([[["Business Name", "Address", "Phone", "Latitude", "Longitude"], ...rows.map((r) => [r.name, r.address, r.phone, r.latitude, r.longitude])].map((l) => l.map(csvCell).join(",")).join("\r\n")], { type: "text/csv;charset=utf-8" });

export const FillZipsDialog = ({ open, onOpenChange, onDone }) => {
  const [missing, setMissing] = useState(null);
  const [missingCity, setMissingCity] = useState(0);
  const [missingStreet, setMissingStreet] = useState(0);
  const [job, setJob] = useState(null);
  const poll = useRef(null);

  const watch = (id) => {
    clearInterval(poll.current);
    poll.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/labels/zips/fill/${id}`);
        setJob(data);
        if (data.status === "done") { clearInterval(poll.current); toast.success(`ZIP codes added to ${data.updated} prospect${data.updated === 1 ? "" : "s"}`); onDone?.(); }
      } catch { clearInterval(poll.current); }
    }, 2000);
  };

  useEffect(() => {
    if (!open) { clearInterval(poll.current); return; }
    setJob(null); setMissing(null);
    api.get("/labels/zips/summary").then(({ data }) => {
      setMissing(data.missing);
      setMissingCity(data.missing_city || 0);
      setMissingStreet(data.missing_street || 0);
      if (data.running_job) { setJob({ id: data.running_job, status: "running", done: 0, total: data.missing }); watch(data.running_job); }
    }).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => clearInterval(poll.current), []);

  const start = async () => {
    try {
      const { data } = await api.post("/labels/zips/fill");
      setJob({ id: data.job_id, status: "running", done: 0, total: data.total, updated: 0, unable: 0 });
      watch(data.job_id);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const running = job?.status === "running";
  const pct = job?.total ? Math.round((job.done / job.total) * 100) : 0;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="fill-zips-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading"><MapPinned className="w-5 h-5 text-primary" />Fill Missing ZIP Codes</DialogTitle>
          <DialogDescription>Finds prospects missing a ZIP, city or street address and looks them up from each prospect's map location and address. A street is only filled when the map location gives a house number and road. About one prospect per second.</DialogDescription>
        </DialogHeader>
        <div className="rounded-xl border border-border p-4 text-sm" data-testid="fill-zips-missing">
          Prospects missing ZIPs: <b className="tabular-nums" data-testid="fill-zips-missing-count">{missing == null ? "…" : missing}</b>
          {missingCity > 0 && <span className="text-muted-foreground" data-testid="fill-zips-missing-city"> · {missingCity} also missing a city</span>}
          {missingStreet > 0 && <span className="text-muted-foreground" data-testid="fill-zips-missing-street"> · {missingStreet} missing a street address</span>}
        </div>
        {job && (
          <div className="space-y-3" data-testid="fill-zips-job">
            {running && (
              <div className="space-y-1.5" data-testid="fill-zips-progress">
                <Progress value={pct} className="h-2" />
                <div className="text-xs text-muted-foreground flex items-center gap-1.5 tabular-nums"><Loader2 className="w-3.5 h-3.5 animate-spin" />Checked {job.done} of {job.total} · you can close this window, it keeps running</div>
              </div>
            )}
            {job.status === "done" && (
              <div className="rounded-xl border border-border p-4 space-y-2" data-testid="fill-zips-result">
                <div className="flex items-center gap-2 text-sm"><CheckCircle2 className="w-4 h-4 text-emerald-500" />Updated: <b data-testid="fill-zips-updated">{job.updated}</b>{job.cities > 0 && <span className="text-muted-foreground" data-testid="fill-zips-cities">(city also filled for {job.cities})</span>}{job.streets > 0 && <span className="text-muted-foreground" data-testid="fill-zips-streets">(street address found for {job.streets})</span>}</div>
                <div className="flex items-center gap-2 text-sm"><AlertTriangle className={`w-4 h-4 ${job.unable ? "text-amber-500" : "text-muted-foreground"}`} />Unable to determine: <b data-testid="fill-zips-unable">{job.unable}</b></div>
                {job.unable > 0 && <Button size="sm" variant="outline" onClick={() => saveBlob(remainingCsv(job.remaining || []), "Remaining_Missing_ZIPs.csv")} data-testid="fill-zips-download-csv"><FileSpreadsheet className="w-4 h-4 mr-1.5" />Remaining_Missing_ZIPs.csv</Button>}
                <div className="text-xs text-muted-foreground" data-testid="fill-zips-audit-note">Logged: "ZIP codes auto-populated from address/location lookup."</div>
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="fill-zips-close">Close</Button>
          <Button onClick={start} disabled={running || !missing} data-testid="fill-zips-start-btn">{running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MapPinned className="w-4 h-4 mr-2" />}Fill Missing ZIP Codes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
